import { GBP_BENCHMARK_TYPICAL } from '../google-ranking/google-ranking-score-model';
import type { GbpCategory } from '../google-ranking/google-ranking-suggestion-values';
import type { BusinessKind } from './business-kind-detection';

/** Review and photo numbers a busy place of this kind usually has (the shape of `GBP_BENCHMARK_TYPICAL`). */
export type BusinessKindTypicalBenchmark = { reviewsTotal: number; reviewsPer30d: number; photosTotal: number };

/** How the AI writers (review replies, Google posts, description) speak about this kind of place. */
export type BusinessKindAiPersona = {
  /** The place with its article, when the Google category says nothing more precise ("a hair salon"). */
  businessPhrase: string;
  /** What a review of this kind talks about, for prompt examples ("a dish, a drink or a server"). */
  exampleThings: string;
  /** What a photo post is about, told to the AI. */
  photoPostAbout: string;
  /** Deterministic photo post (AI off or failed): "<headline> at <salon in city>! <body>". */
  photoPostHeadline: string;
  photoPostBody: string;
};

/**
 * Per-kind tuning of the Google tools. One entry per kind, so a new field is filled for every kind or the
 * build fails. Default map scan keywords are built per kind from the main Google category
 * (`defaultRankKeywordsFor`), the AI wording from `aiPersona` (`businessKindPromptPersona`), the photo library
 * subjects from `gbpLibrarySubjectsFor`.
 */
export type BusinessKindProfile = {
  /** Shown to the agency next to the salon ("Hair salon"). */
  label: string;
  /** How copy and AI prompts name the place ("your salon", "your restaurant"). */
  noun: string;
  /** Who does the work, singular ("technician", "stylist"). */
  staffTerm: string;
  /** Google Places type for nearby-competitor reads when the listing's own type is unknown; null = no type filter. */
  placesTypeFallback: string | null;
  /** The salon runs its service menu in Hayven (hair salons and restaurants use only the Google tools). */
  hasHayvenMenu: boolean;
  /** Placeholder of "Track a keyword": a longer search phrase this kind of customer types into Maps. */
  rankKeywordExample: string;
  /**
   * The main Google category this kind should have, suggested when the listing's main category is outside the
   * kind (an agency override). null = no single right category (OTHER), so none is suggested or graded.
   * The id is resolved live through Google's category list; this one is the fallback.
   */
  canonicalPrimaryCategory: GbpCategory | null;
  /**
   * Numbers the profile score compares with until the weekly map scan has top-3 data. Starting values for the
   * non-nail kinds, tunable once real listings are graded.
   */
  typicalBenchmark: BusinessKindTypicalBenchmark;
  /** How copy names those typical places ("busy hair salons usually get about 8"). */
  typicalLabel: string;
  aiPersona: BusinessKindAiPersona;
};

export const BUSINESS_KIND_PROFILES: Record<BusinessKind, BusinessKindProfile> = {
  NAIL: {
    label: 'Nail salon',
    noun: 'salon',
    staffTerm: 'technician',
    placesTypeFallback: 'nail_salon',
    hasHayvenMenu: true,
    rankKeywordExample: 'dip powder nails',
    canonicalPrimaryCategory: { name: 'gcid:nail_salon', displayName: 'Nail salon' },
    typicalBenchmark: GBP_BENCHMARK_TYPICAL,
    typicalLabel: 'busy nail salons',
    aiPersona: {
      businessPhrase: 'a nail salon',
      exampleThings: 'a service, a technician or the result',
      photoPostAbout: 'a new photo of the salon\'s work, shown with the post ("new looks this week")',
      photoPostHeadline: 'New looks this week',
      photoPostBody:
        'Our team has been busy creating fresh nail designs — here is one of our favorites. Come see us for your next set whenever you are ready.',
    },
  },
  HAIR: {
    label: 'Hair salon',
    noun: 'salon',
    staffTerm: 'stylist',
    placesTypeFallback: 'hair_salon',
    hasHayvenMenu: false,
    rankKeywordExample: 'balayage near me',
    canonicalPrimaryCategory: { name: 'gcid:hair_salon', displayName: 'Hair salon' },
    typicalBenchmark: { reviewsTotal: 200, reviewsPer30d: 8, photosTotal: 150 },
    typicalLabel: 'busy hair salons',
    aiPersona: {
      businessPhrase: 'a hair salon',
      exampleThings: 'a cut, a color, a style or a stylist',
      photoPostAbout: 'a new photo of the salon\'s work (a cut, color or style), shown with the post ("new looks this week")',
      photoPostHeadline: 'New looks this week',
      photoPostBody:
        'Our team has been busy creating fresh cuts and styles — here is one of our favorites. Come see us for your next appointment whenever you are ready.',
    },
  },
  RESTAURANT: {
    label: 'Restaurant',
    noun: 'restaurant',
    staffTerm: 'staff',
    placesTypeFallback: 'restaurant',
    hasHayvenMenu: false,
    rankKeywordExample: 'takeout near me',
    canonicalPrimaryCategory: { name: 'gcid:restaurant', displayName: 'Restaurant' },
    typicalBenchmark: { reviewsTotal: 600, reviewsPer30d: 25, photosTotal: 400 },
    typicalLabel: 'busy restaurants',
    aiPersona: {
      businessPhrase: 'a restaurant',
      exampleThings: 'a dish, a drink, the service or the wait',
      photoPostAbout: 'a new photo from the restaurant (a dish, a drink or the dining room), shown with the post ("fresh from our kitchen")',
      photoPostHeadline: 'Fresh from our kitchen',
      photoPostBody:
        'Here is one of our favorite plates this week, made fresh the way our regulars love it. Stop by and taste it for yourself whenever you are hungry.',
    },
  },
  OTHER: {
    label: 'Other business',
    noun: 'business',
    staffTerm: 'staff',
    placesTypeFallback: null,
    hasHayvenMenu: false,
    rankKeywordExample: 'open now near me',
    canonicalPrimaryCategory: null,
    typicalBenchmark: { reviewsTotal: 150, reviewsPer30d: 6, photosTotal: 100 },
    typicalLabel: 'busy local businesses',
    aiPersona: {
      businessPhrase: 'a local business',
      exampleThings: 'a service, a product or a team member',
      photoPostAbout: 'a new photo from the business, shown with the post ("new this week")',
      photoPostHeadline: 'New this week',
      photoPostBody:
        'Here is a fresh look at what our team has been up to lately. Come see us whenever you are ready — we would love to welcome you.',
    },
  },
};
